const EVENT_TYPE_LABELS = {
  ranking: "Ranking",
  lojinha: "Lojinha",
  loja: "Lojinha",
  ticket: "Lojinha",
};

export const getNumericValue = (value) =>
  Number(String(value).replace(/\./g, "").replace(",", ".")) || 0;

export const parseMetricNumber = (value) => {
  if (value === undefined || value === null || value === "") {
    return Number.NaN;
  }

  const normalizedText = String(value).trim();
  const hasThousandSeparators = /^\d{1,3}(\.\d{3})+(,\d+)?$/.test(
    normalizedText
  );

  return typeof value === "number"
    ? value
    : Number(
        (hasThousandSeparators
          ? normalizedText.replace(/\./g, "")
          : normalizedText
        ).replace(",", ".")
      );
};

export const getTimeValue = (value) => {
  const parts = String(value).split(":").map(Number);

  return parts.reduce((total, part) => total * 60 + (part || 0), 0);
};

export const formatNumber = (value) => {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  const numericValue = parseMetricNumber(value);

  return Number.isNaN(numericValue)
    ? String(value)
    : numericValue.toLocaleString("pt-BR", {
        maximumFractionDigits: 2,
      });
};

export const formatSecondsAsTime = (value) => {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  if (String(value).includes(":")) {
    return String(value);
  }

  const numericValue = parseMetricNumber(value);

  if (Number.isNaN(numericValue)) {
    return String(value);
  }

  const totalSeconds = Math.max(0, Math.round(numericValue));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0"
  )}:${String(seconds).padStart(2, "0")}`;
};

export const formatDisplayDate = (date) => {
  if (!date) {
    return "";
  }

  const dateString = String(date);
  const isoDateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    return `${day}/${month}/${year}`;
  }

  return dateString;
};

export const getDateFromDisplayValue = (date) => {
  const [day, month, year] = String(date || "")
    .split("/")
    .map(Number);

  if (!day || !month || !year) {
    return null;
  }

  const parsedDate = new Date(year, month - 1, day);
  parsedDate.setHours(0, 0, 0, 0);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export const getDateTimestamp = (date) =>
  getDateFromDisplayValue(date)?.getTime() || 0;

export const getEventStatus = ({ startDate, endDate }) => {
  const startDateValue = getDateFromDisplayValue(startDate);
  const endDateValue = getDateFromDisplayValue(endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (endDateValue && endDateValue < today) {
    return "expired";
  }

  if (startDateValue && startDateValue > today) {
    return "soon";
  }

  return "active";
};

export const normalizeEventType = (type) => {
  const eventType = String(type || "").trim();
  const eventTypeKey = eventType.toLowerCase();

  return EVENT_TYPE_LABELS[eventTypeKey] || eventType || "Ranking";
};

export const getBackendEventType = (type) => {
  const eventTypeKey = String(type || "").trim().toLowerCase();

  return eventTypeKey === "lojinha" || eventTypeKey === "loja"
    ? "ticket"
    : eventTypeKey;
};

export const getEventTypeValue = (event) =>
  event?.originalEvent?.type || event?.category;

export const getEventBackendType = (event) =>
  getBackendEventType(getEventTypeValue(event));

export const getApiDateFromDisplayValue = (date) => {
  const dateMatch = String(date || "").match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?$/
  );

  if (!dateMatch) {
    return "";
  }

  const [, day, month, year, hour = "00", minute = "00"] = dateMatch;

  return `${year}-${month}-${day}T${hour}:${minute}:00`;
};

export const getOriginalEventEndDateValue = (event) =>
  event?.originalEvent?.expiration_date ||
  event?.originalEvent?.end_date ||
  event?.originalEvent?.endDate;

export const getEventExpirationDateApiValue = (event) =>
  getOriginalEventEndDateValue(event) ||
  getApiDateFromDisplayValue(event?.endDate);

export const getEventTitleValue = (event) =>
  event?.originalEvent?.title ||
  event?.originalEvent?.name ||
  event?.name ||
  "";

export const getEventStartDateValue = (event) =>
  event?.originalEvent?.start_date ||
  event?.originalEvent?.startDate ||
  event?.startDate;

export const getEventEndDateValue = (event) =>
  getOriginalEventEndDateValue(event) || event?.endDate;

export const formatParticipants = (participants) => {
  if (
    participants === undefined ||
    participants === null ||
    participants === ""
  ) {
    return "0";
  }

  const normalizedValue = Number(
    String(participants).replace(/\./g, "").replace(",", ".")
  );

  return Number.isNaN(normalizedValue)
    ? String(participants)
    : normalizedValue.toLocaleString("pt-BR");
};

export const formatExpirationDate = (date) => {
  if (!date) {
    return "";
  }

  const dateString = String(date).trim();
  const displayDateMatch = dateString.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  if (displayDateMatch) {
    const [, day, month, year] = displayDateMatch;
    return `${year}-${month}-${day}`;
  }

  return dateString;
};
